import { useState, useEffect } from 'react'

const CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#@$%&×◆'
const DEFAULT_STATUS = 'Initiating secure verification'

function useScramble(target) {
    const [display, setDisplay] = useState(target ?? DEFAULT_STATUS)

    useEffect(() => {
        const text = target || DEFAULT_STATUS
        let frame = 0
        const totalFrames = text.length * 3

        const tick = setInterval(() => {
            frame++
            const resolved = Math.floor((frame / totalFrames) * text.length)
            setDisplay(
                text.split('').map((ch, i) => {
                    if (ch === ' ') return ' '
                    if (i < resolved) return ch
                    return CHARS[Math.floor(Math.random() * CHARS.length)]
                }).join('')
            )
            if (frame >= totalFrames) {
                clearInterval(tick)
                setDisplay(text)
            }
        }, 28)

        return () => clearInterval(tick)
    }, [target])

    return display
}

const EQ_BARS = [
    { h: 10, delay: '0.00s' },
    { h: 18, delay: '0.20s' },
    { h: 14, delay: '0.40s' },
    { h: 20, delay: '0.10s' },
    { h: 12, delay: '0.30s' },
]

const VerificationOverlay = ({ progressStatus }) => {
    const display = useScramble(progressStatus)

    return (
        <div
            className="fixed inset-0 z-[9999] flex flex-col items-center justify-center px-6"
            style={{ background: 'rgba(7, 12, 30, 0.97)', backdropFilter: 'blur(12px)' }}
        >
            <style>{`
                @keyframes scanDown {
                    0%   { transform: translateY(-1px); opacity: 0.9; }
                    80%  { opacity: 0.6; }
                    100% { transform: translateY(160px); opacity: 0; }
                }
                @keyframes eqPulse {
                    0%, 100% { transform: scaleY(0.3); }
                    50%      { transform: scaleY(1); }
                }
                @keyframes borderShimmer {
                    0%   { opacity: 0.5; }
                    50%  { opacity: 1; }
                    100% { opacity: 0.5; }
                }
                @keyframes subtleFloat {
                    0%, 100% { transform: translateY(0); }
                    50%      { transform: translateY(-4px); }
                }
                .scan-line { animation: scanDown 3s linear infinite; }
                .border-shimmer { animation: borderShimmer 2.4s ease-in-out infinite; }
                .icon-float { animation: subtleFloat 4s ease-in-out infinite; }
            `}</style>

            {/* Ambient glow behind card */}
            <div
                className="absolute w-96 h-96 rounded-full pointer-events-none"
                style={{
                    background: 'radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%)',
                    filter: 'blur(40px)',
                }}
            />

            {/* Icon */}
            <div className="icon-float relative flex items-center justify-center mb-5">
                <div
                    className="absolute w-14 h-14 rounded-full animate-ping"
                    style={{ background: 'rgba(59,130,246,0.12)', animationDuration: '2.5s' }}
                />
                <div
                    className="relative w-9 h-9 rounded-full flex items-center justify-center"
                    style={{
                        background: 'rgba(15,30,70,0.9)',
                        boxShadow: '0 0 0 1px rgba(59,130,246,0.4), 0 0 16px rgba(59,130,246,0.2)',
                    }}
                >
                    <svg className="w-4 h-4 text-blue-300" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round"
                            d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.96 11.96 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.153c-3.196 0-6.1-1.248-8.25-3.286z"
                        />
                    </svg>
                </div>
            </div>

            {/* Section label */}
            <p className="text-[10px] font-bold tracking-[0.3em] uppercase mb-5"
                style={{ color: 'rgba(96,165,250,0.6)' }}>
                Identity Verification
            </p>

            {/* Main card */}
            <div className="relative w-full max-w-sm">

                {/* Animated glow border */}
                <div
                    className="border-shimmer absolute -inset-px rounded-2xl pointer-events-none"
                    style={{
                        background: 'linear-gradient(135deg, rgba(59,130,246,0.6), transparent 50%, rgba(99,102,241,0.5))',
                        borderRadius: '17px',
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude',
                        padding: '1px',
                    }}
                />

                <div
                    className="relative rounded-2xl overflow-hidden"
                    style={{
                        background: 'rgba(12, 22, 52, 0.85)',
                        boxShadow: '0 0 0 1px rgba(59,130,246,0.18), 0 0 40px rgba(59,130,246,0.08), inset 0 1px 0 rgba(255,255,255,0.04)',
                    }}
                >
                    {/* Content + scan line */}
                    <div className="relative overflow-hidden">

                        {/* Scan line */}
                        <div
                            className="scan-line absolute inset-x-0 top-0 h-px pointer-events-none z-10"
                            style={{
                                background: 'linear-gradient(to right, transparent 0%, rgba(96,165,250,0.7) 40%, rgba(165,180,252,0.9) 50%, rgba(96,165,250,0.7) 60%, transparent 100%)',
                                boxShadow: '0 0 8px rgba(96,165,250,0.5)',
                            }}
                        />

                        <div className="px-6 pt-5 pb-6">

                            {/* Live badge */}
                            <div className="flex items-center gap-2 mb-4">
                                <span
                                    className="w-1.5 h-1.5 rounded-full animate-pulse"
                                    style={{ background: '#34d399', boxShadow: '0 0 6px #34d399', animationDuration: '1.4s' }}
                                />
                                <span className="text-[9px] font-bold tracking-[0.22em] uppercase"
                                    style={{ color: 'rgba(52,211,153,0.75)' }}>
                                    Live Status
                                </span>
                            </div>

                            {/* Scrambling status text */}
                            <p
                                className="font-mono text-[15px] font-medium leading-relaxed min-h-[3rem]"
                                style={{
                                    color: 'rgba(226,232,255,0.95)',
                                    textShadow: '0 0 24px rgba(96,165,250,0.35)',
                                    letterSpacing: '0.02em',
                                }}
                            >
                                {display}
                            </p>

                        </div>
                    </div>

                    {/* Divider */}
                    <div style={{ height: '1px', background: 'rgba(59,130,246,0.1)' }} />

                    {/* Bottom bar */}
                    <div
                        className="px-6 py-3 flex items-center justify-between"
                        style={{ background: 'rgba(8,16,40,0.6)' }}
                    >
                        <span
                            className="text-[9px] font-mono tracking-widest"
                            style={{ color: 'rgba(148,163,184,0.22)' }}
                        >
                            SEC · VID · {new Date().getFullYear()}
                        </span>

                        {/* Equalizer bars */}
                        <div className="flex items-end gap-[3px]" style={{ height: '20px' }}>
                            {EQ_BARS.map(({ h, delay }, i) => (
                                <div
                                    key={i}
                                    className="w-[3px] rounded-t-[1px] origin-bottom"
                                    style={{
                                        height: `${h}px`,
                                        animationName: 'eqPulse',
                                        animationDuration: '1.1s',
                                        animationTimingFunction: 'ease-in-out',
                                        animationIterationCount: 'infinite',
                                        animationDelay: delay,
                                        background: `linear-gradient(to top, rgba(59,130,246,${0.5 + i * 0.08}), rgba(165,180,252,0.85))`,
                                    }}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom hint */}
            <p className="mt-6 text-[11px] tracking-wide text-center"
                style={{ color: 'rgba(148,163,184,0.3)' }}>
                Securing your verification results
            </p>
        </div>
    )
}

export default VerificationOverlay
